import React from 'react';
import { pdf } from '@react-pdf/renderer';
import ServiceSheetPdf from '../ServiceSheetPdf';
import RefrigerationPdf from '../RefrigerationPdf';
import PreSeasonPdf from '../PreSeasonPdf';
import SalesOrderPdf from '../SalesOrderPdf';
import StorageInspectionPdf from '../StorageInspectionPdf';
import ChemAppsAgreementsPdf from '../chemApps/ChemAppsAgreementsPdf';
import {
    transformServiceSheetData,
    transformRefrigerationData,
    transformPreSeasonData,
    transformSalesOrderData,
    transformInspectionData,
    getSheetTypeForPdf,
    generatePdfFilename
} from '../../utils/pdfDataTransformer';

interface PdfGeneratorButtonProps {
    sheetType: string;
    data: any;
    user?: any;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

/**
 * Utility component to generate and download PDFs
 * This component doesn't render anything, it's just used for its functionality
 */
const PdfGeneratorButton: React.FC<PdfGeneratorButtonProps> = ({
    sheetType,
    data,
    user,
    onSuccess,
    onError
}) => {
    return null; // This component doesn't render anything
};

/**
 * Download a blob as a file using the browser's download API
 */
const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Check if the service date is before the cutoff date (7/1/2025)
 * Returns true if the date is before the cutoff, false otherwise
 */
const isBeforeCutoffDate = (serviceDate: string | Date | null | undefined): boolean => {
    if (!serviceDate) {
        return false; // If no date, allow download
    }

    const cutoffDate = new Date('2025-07-01T00:00:00');
    const serviceDateObj = new Date(serviceDate);

    // Check if the date is valid
    if (isNaN(serviceDateObj.getTime())) {
        return false; // If invalid date, allow download
    }

    return serviceDateObj < cutoffDate;
};

/**
 * Display message for old records that require calling Agri-Stor
 */
const showOldRecordMessage = () => {
    alert('Cannot download PDF. This is not an error. Please call Agri-Stor to request a copy of this receipt.');
};

/**
 * Function to generate and download a service sheet PDF
 */
export const generateServiceSheetPdf = async (
    sheetType: string,
    rawData: any,
    user?: any,
): Promise<void> => {
    try {
        // Check if the service date is before the cutoff date
        const serviceDate = rawData.dateServiced || rawData.dateserviced;
        if (isBeforeCutoffDate(serviceDate)) {
            showOldRecordMessage();
            return;
        }

        console.log('[PDF] Starting PDF generation for', sheetType, rawData);

        // Determine the PDF sheet type
        const pdfSheetType = getSheetTypeForPdf(sheetType);

        // Transform the data
        const transformedData = transformServiceSheetData(rawData, pdfSheetType);

        console.log('[PDF] Transformed data:', transformedData);

        // Generate the PDF document
        const pdfDoc = (
            <ServiceSheetPdf
                data={transformedData}
                user={user}
                sheetType={pdfSheetType}
            />
        );

        // Convert to blob
        const blob = await pdf(pdfDoc).toBlob();

        // Generate filename and download
        const filename = generatePdfFilename(sheetType, rawData);
        downloadBlob(blob, filename);

        console.log('[PDF] Successfully generated and downloaded:', filename);
    } catch (error) {
        console.error('[PDF] Error generating PDF:', error);
        throw error;
    }
};

/**
 * Function to generate and download a refrigeration sheet PDF
 */
export const generateRefrigerationPdf = async (
    sheetType: string,
    rawData: any,
    user?: any,
): Promise<void> => {
    try {
        // Check if the service date is before the cutoff date
        const serviceDate = rawData.dateServiced || rawData.dateserviced;
        if (isBeforeCutoffDate(serviceDate)) {
            showOldRecordMessage();
            return;
        }

        console.log('[PDF] Starting Refrigeration PDF generation', rawData);

        const transformedData = transformRefrigerationData(rawData);
        console.log('[PDF] Transformed refrigeration data:', transformedData);

        const pdfDoc = <RefrigerationPdf data={transformedData} user={user} />;
        const blob = await pdf(pdfDoc).toBlob();
        const filename = generatePdfFilename(sheetType, rawData);
        downloadBlob(blob, filename);

        console.log('[PDF] Successfully generated and downloaded:', filename);
    } catch (error) {
        console.error('[PDF] Error generating refrigeration PDF:', error);
        throw error;
    }
};

/**
 * Function to generate and download a preseason sheet PDF
 */
export const generatePreSeasonPdf = async (
    sheetType: string,
    rawData: any,
    user?: any,
): Promise<void> => {
    try {
        // Check if the service date is before the cutoff date
        const serviceDate = rawData.dateServiced || rawData.dateserviced;
        if (isBeforeCutoffDate(serviceDate)) {
            showOldRecordMessage();
            return;
        }

        console.log('[PDF] Starting PreSeason PDF generation', rawData);

        const transformedData = transformPreSeasonData(rawData);
        console.log('[PDF] Transformed preseason data:', transformedData);

        const pdfDoc = <PreSeasonPdf data={transformedData} user={user} />;
        const blob = await pdf(pdfDoc).toBlob();
        const filename = generatePdfFilename(sheetType, rawData);
        downloadBlob(blob, filename);

        console.log('[PDF] Successfully generated and downloaded:', filename);
    } catch (error) {
        console.error('[PDF] Error generating preseason PDF:', error);
        throw error;
    }
};

/**
 * Function to generate and download a sales order PDF
 */
export const generateSalesOrderPdf = async (
    sheetType: string,
    rawData: any,
    user?: any,
): Promise<void> => {
    try {
        // Check if the service date is before the cutoff date
        const serviceDate = rawData.dateServiced || rawData.dateserviced;
        if (isBeforeCutoffDate(serviceDate)) {
            showOldRecordMessage();
            return;
        }

        console.log('[PDF] Starting Sales Order PDF generation', rawData);

        const transformedData = transformSalesOrderData(rawData);
        console.log('[PDF] Transformed sales order data:', transformedData);

        const pdfDoc = <SalesOrderPdf data={transformedData} user={user} />;
        const blob = await pdf(pdfDoc).toBlob();
        const filename = generatePdfFilename(sheetType, rawData);
        downloadBlob(blob, filename);

        console.log('[PDF] Successfully generated and downloaded:', filename);
    } catch (error) {
        console.error('[PDF] Error generating sales order PDF:', error);
        throw error;
    }
};

/**
 * Function to generate and download an inspection PDF
 */
export const generateInspectionPdf = async (
    sheetType: string,
    rawData: any,
    user?: any,
): Promise<void> => {
    try {
        // Check if the service date is before the cutoff date
        const serviceDate = rawData.dateServiced || rawData.dateserviced;
        if (isBeforeCutoffDate(serviceDate)) {
            showOldRecordMessage();
            return;
        }

        console.log('[PDF] Starting Inspection PDF generation', rawData);

        const transformedData = transformInspectionData(rawData);
        console.log('[PDF] Transformed inspection data:', transformedData);

        const pdfDoc = <StorageInspectionPdf data={transformedData} user={user} />;
        const blob = await pdf(pdfDoc).toBlob();
        const filename = generatePdfFilename(sheetType, rawData);
        downloadBlob(blob, filename);

        console.log('[PDF] Successfully generated and downloaded:', filename);
    } catch (error) {
        console.error('[PDF] Error generating inspection PDF:', error);
        throw error;
    }
};

/**
 * Function to generate and download a ChemApps agreement PDF
 */
export const generateChemAppsAgreementPdf = async (
    sheetType: string,
    rawData: any,
    user?: any,
): Promise<void> => {
    try {
        // Check if the service date is before the cutoff date
        const serviceDate = rawData.dateServiced || rawData.dateserviced || rawData.date;
        if (isBeforeCutoffDate(serviceDate)) {
            showOldRecordMessage();
            return;
        }

        console.log('[PDF] Starting ChemApps Agreement PDF generation', rawData);

        // Determine the agreement sheet type
        let agreementSheetType: 'chemApps' | 'disinfectant' | 'sprayBar' = 'chemApps';
        if (sheetType === 'disinfectantAgreement') {
            agreementSheetType = 'disinfectant';
        } else if (sheetType === 'sprayBarAgreement') {
            agreementSheetType = 'sprayBar';
        }

        // Structure the data for the PDF component
        const pdfData = {
            agreement: rawData,
            sheetType: agreementSheetType,
            instructions: rawData.instructions || '',
        };

        console.log('[PDF] Transformed chemApps agreement data:', pdfData);

        const pdfDoc = <ChemAppsAgreementsPdf data={pdfData} customerSignature={undefined} />;
        const blob = await pdf(pdfDoc).toBlob();
        const filename = generatePdfFilename(sheetType, rawData);
        downloadBlob(blob, filename);

        console.log('[PDF] Successfully generated and downloaded:', filename);
    } catch (error) {
        console.error('[PDF] Error generating chemApps agreement PDF:', error);
        throw error;
    }
};

export default PdfGeneratorButton;
